import { z } from 'zod';

// ── Scope discriminator ────────────────────────────────────────────────────
// A file is attached to exactly one "scope" — a task it belongs to, a project
// it belongs to (project-level artifacts like the final brief), or a comment
// (Phase 4 follow-up — files on comments use the `task_comment_files` join
// table once wired).

export const FileScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('task'), taskId: z.string().uuid() }),
  z.object({ kind: z.literal('project'), projectId: z.string().uuid() }),
  /** Chat uploads are bound to the concrete topic. The server derives the
   *  team/project from the topic and re-validates that binding on attach. */
  z.object({ kind: z.literal('topic'), topicId: z.string().uuid() }),
  z.object({ kind: z.literal('dm'), threadId: z.string().uuid(), teamId: z.string().uuid() }),
  // Team scope is retained for explicit team-owned assets such as avatars.
  // Conversation attachments use the topic/DM variants above so their source
  // ACL is durable before any message attachment row exists.
  z.object({ kind: z.literal('team'), teamId: z.string().uuid() }),
]);
export type FileScope = z.infer<typeof FileScopeSchema>;

// ── Presign-upload request ─────────────────────────────────────────────────
// Client posts metadata; server returns a one-time proxy PUT capability. The
// DB row reserves quota immediately but is not listable/readable until the
// exact upload commits. Abandoned reservations expire through the opt-in
// lifecycle worker.

export const PresignUploadInputSchema = z.object({
  name: z.string().min(1).max(255),
  mime: z.string().min(1).max(120),
  /** File size in bytes.  Hard cap at 100 MiB for v1 — voice / video later. */
  sizeBytes: z.number().int().nonnegative().max(100 * 1024 * 1024),
  scope: FileScopeSchema,
  /** Security-sensitive purpose is explicit. Missing means ordinary private
   *  attachment; legacy clients never acquire a durable avatar capability. */
  purpose: z.enum(['attachment', 'avatar']).optional(),
});
export type PresignUploadInput = z.infer<typeof PresignUploadInputSchema>;

export const PresignUploadResultSchema = z.object({
  file: z.lazy(() => FileSchema),
  /** Signed PUT URL (our API's `/content` proxy).  Valid for 15 min.  Send the
   *  file as the raw request body with `Content-Type: application/octet-stream`
   *  and an exact `Content-Length`; the stored `mime` (from presign) is what S3
   *  and downloads use, never the PUT's own Content-Type. */
  uploadUrl: z.string().url(),
});
export type PresignUploadResult = z.infer<typeof PresignUploadResultSchema>;

// ── File projection (what list / get return) ───────────────────────────────

export const FileSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  uploadedBy: z.string().uuid(),
  name: z.string(),
  mime: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  taskId: z.string().uuid().nullable(),
  projectId: z.string().uuid().nullable(),
  /** Project folder id when filed under a folder; NULL = project root.
   *  Migration 0053 introduces the folder hierarchy; pre-existing files
   *  default to `null` (root of the project's file tree). */
  folderId: z.string().uuid().nullable(),
  /** Omitted by legacy servers; new servers expose readiness so pending
   *  reservations are never mistaken for downloadable objects. */
  uploadState: z.enum(['pending', 'uploading', 'committed', 'delete_pending', 'deleted']).optional(),
  createdAt: z.string(),
  /** Pre-rendered download URL (15-min TTL).  Refresh via `/api/files/:id/download-url`
   *  before it expires. */
  downloadUrl: z.string().url().nullable(),
});
export type FileProjection = z.infer<typeof FileSchema>;

export const FileListSchema = z.array(FileSchema);

// ── Download URL refresh ───────────────────────────────────────────────────

export const FileDownloadUrlSchema = z.object({
  url: z.string().url(),
  /** Seconds from now until the URL expires. */
  expiresInSeconds: z.number().int().positive(),
});
export type FileDownloadUrlProjection = z.infer<typeof FileDownloadUrlSchema>;
